import { useSelector } from "@xstate/react";
import { type Actor, assign, createActor, setup } from "xstate";

import type { TranscriptItem } from "@elation/api-client/src/stitch-web/models";

// Import from the domain barrel, not the feature barrel: the latter re-exports
// `useVisitRecordingSession`, which pulls `@elation/transcribe` ->
// `extendable-media-recorder` into this module graph. That package touches
// `URL.createObjectURL` at eval time, which jsdom does not implement, so the
// feature barrel breaks this file's unit tests at import time.
import { matchesActiveEncounter } from "@src/features/encounters/domain";
import { appBus } from "@src/features/events";

/**
 * Context for the active encounter's transcript.
 *
 * Tracked here (not React state) so subscriptions are registered before
 * render commits — closing the lost-update window the previous
 * `useState` + `useEffect` pattern had — and so the polling-vs-SSE race
 * guard (`lastSseAt` vs `dataUpdatedAt`) lives alongside the data it
 * protects.
 */
export type TranscriptContext = {
  items: TranscriptItem[];
  scribingSessionId: string | null;
  /** Wall-clock millis of the last SSE event. Compared against React Query's
   *  `dataUpdatedAt` to drop a polled response that was in-flight when an
   *  SSE update landed. */
  lastSseAt: number;
};

/**
 * Events accepted by the transcript machine.
 *
 * - `SNAPSHOT_RECEIVED` — the server pushed the full ordered list (e.g. via
 *   the future REST seed endpoint, or a re-publish on the SSE channel).
 * - `ITEM_CREATED` — a single new final item arrived as a delta.
 * - `POLL_APPLIED` — React Query's safety-net poll has fresh data; the
 *   `dataUpdatedAt` is supplied so the machine can discard stale polls.
 * - `RESET` — encounter context changed (patient switch, encounter id
 *   change). Drops items + sessionId so a fresh popover never reads
 *   another patient's transcript.
 */
export type TranscriptEvent =
  | { type: "SNAPSHOT_RECEIVED"; items: TranscriptItem[] }
  | { type: "ITEM_CREATED"; item: TranscriptItem }
  | { type: "POLL_APPLIED"; items: TranscriptItem[]; dataUpdatedAt: number }
  | { type: "RESET" };

export const transcriptMachine = setup({
  types: {
    context: {} as TranscriptContext,
    events: {} as TranscriptEvent,
  },
  actions: {
    replaceItems: assign(({ event }) => {
      if (event.type !== "SNAPSHOT_RECEIVED") return {};
      return {
        items: event.items,
        scribingSessionId: event.items[0]?.scribingSessionId ?? null,
        lastSseAt: Date.now(),
      };
    }),
    appendItem: assign(({ context, event }) => {
      if (event.type !== "ITEM_CREATED") return {};
      // Dedupe by id: a delta that overlaps with a snapshot delivered on
      // (re)connect would otherwise double-render the same item.
      if (context.items.some((existing) => existing.id === event.item.id)) {
        return {};
      }
      return {
        items: [...context.items, event.item],
        scribingSessionId:
          event.item.scribingSessionId ?? context.scribingSessionId,
        lastSseAt: Date.now(),
      };
    }),
    applyPolled: assign(({ context, event }) => {
      if (event.type !== "POLL_APPLIED") return {};
      // SSE wins races: a polled response that was in-flight when an SSE
      // event arrived could be stale by the time it lands. Skip it.
      if (context.lastSseAt > event.dataUpdatedAt) return {};
      return {
        items: event.items,
        scribingSessionId:
          event.items[0]?.scribingSessionId ?? context.scribingSessionId,
      };
    }),
    reset: assign(() => ({
      items: [],
      scribingSessionId: null,
      lastSseAt: 0,
    })),
  },
}).createMachine({
  id: "transcript",
  context: { items: [], scribingSessionId: null, lastSseAt: 0 },
  on: {
    SNAPSHOT_RECEIVED: { actions: { type: "replaceItems" } },
    ITEM_CREATED: { actions: { type: "appendItem" } },
    POLL_APPLIED: { actions: { type: "applyPolled" } },
    RESET: { actions: { type: "reset" } },
  },
});

/**
 * Singleton actor that holds the active encounter's transcript items.
 *
 * Started eagerly so external callers (`sendTranscriptEvent`, the bus
 * handlers below) can rely on it being live before any React component
 * subscribes. Bus → actor wiring lives below; encounter-id changes feed in
 * via `client.router_params_changed`, which `encounterActor` also listens
 * to — so transcript reset and encounter reset stay in lock-step.
 */
export const transcriptActor: Actor<typeof transcriptMachine> =
  createActor(transcriptMachine).start();

export function sendTranscriptEvent(event: TranscriptEvent): void {
  transcriptActor.send(event);
}

const onTranscriptSnapshot = (payload: {
  encounterId: number;
  items: TranscriptItem[];
}) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  transcriptActor.send({ type: "SNAPSHOT_RECEIVED", items: payload.items });
};

const onTranscriptItemCreated = (payload: {
  encounterId: number;
  item: TranscriptItem;
}) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  transcriptActor.send({ type: "ITEM_CREATED", item: payload.item });
};

const onParamsChanged = () => {
  transcriptActor.send({ type: "RESET" });
};

const onSearchParamsChanged = () => {
  transcriptActor.send({ type: "RESET" });
};

appBus.on("transcript.snapshot", onTranscriptSnapshot);
appBus.on("transcript.item.created", onTranscriptItemCreated);
appBus.on("client.router_params_changed", onParamsChanged);
appBus.on("client.router_search_params_changed", onSearchParamsChanged);

export function useTranscriptItems(): TranscriptItem[] {
  return useSelector(transcriptActor, (s) => s.context.items);
}

export function useTranscriptScribingSessionId(): string | null {
  return useSelector(transcriptActor, (s) => s.context.scribingSessionId);
}

/**
 * Tear down the bus subscriptions and stop the actor. Intended for test
 * environments that need a fully-isolated fixture; production code should
 * never call this.
 */
export function __resetForTesting(): void {
  appBus.off("transcript.snapshot", onTranscriptSnapshot);
  appBus.off("transcript.item.created", onTranscriptItemCreated);
  appBus.off("client.router_params_changed", onParamsChanged);
  appBus.off("client.router_search_params_changed", onSearchParamsChanged);

  transcriptActor.stop();
}
